import React, { createContext, useState, useCallback } from "react";

export const ToastContext = createContext();

/**
 * ToastProvider - Manages toast notifications globally
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  /**
   * Add a toast to the queue
   * @param {string} message - Toast message
   * @param {string} variant - success, error, warning, critical, info
   * @param {number} duration - Auto-dismiss duration in ms (0 to disable)
   * @param {object} options - Additional options { onRetry, retryLabel }
   */
  const addToast = useCallback(
    (message, variant = "success", duration = 3000, options = {}) => {
      const id = Date.now() + Math.random();
      const newToast = {
        id,
        message,
        variant,
        duration,
        onRetry: options.onRetry,
        retryLabel: options.retryLabel,
      };

      setToasts((prevToasts) => [...prevToasts, newToast]);

      // Auto-remove toast after duration
      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }

      return id;
    },
    [],
  );

  /**
   * Remove a toast by id
   */
  const removeToast = useCallback((id) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  /**
   * Clear all toasts
   */
  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider
      value={{ toasts, addToast, removeToast, clearToasts }}
    >
      {children}
    </ToastContext.Provider>
  );
}
